import { gql, useApolloClient, useQuery } from '@apollo/client'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { GetUserPagePreferenceDocument } from '@/graphql/purchase-plus/generated/getUserPagePreference.generated'
import { Preference } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'

const PREFERENCE_FRAGMENT = gql`
  fragment PreferenceFragment on Preference {
    key
    value
  }
`

interface UseGetUserPagePreferenceProps {
  key: string
  userId?: number
  skip?: boolean
}

export function useGetUserPagePreference({ key, userId, skip }: UseGetUserPagePreferenceProps) {
  const { user } = useSessionUser()
  const client = useApolloClient()

  const cacheId = useMemo(() => client.cache.identify({ __typename: 'Preference', key }), [client.cache, key])

  // Read preference from cache
  const readFromCache = useCallback((): Preference | null => {
    if (skip) return null
    try {
      return client.readFragment<Preference>({
        id: cacheId,
        fragment: PREFERENCE_FRAGMENT,
      })
    } catch {
      return null
    }
  }, [client, cacheId, skip])

  // Subscribe to cache changes by watching the fragment directly
  // We watch a minimal query that includes the Preference type
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const observable = client.watchFragment({
        fragment: PREFERENCE_FRAGMENT,
        from: { __typename: 'Preference', key },
      })
      const subscription = observable.subscribe({
        next: onStoreChange,
      })
      return () => subscription.unsubscribe()
    },
    [client, key]
  )

  const cachedPreference = useSyncExternalStore(subscribe, readFromCache, readFromCache)

  // Only run the query if not found in cache
  const { data, loading, error } = useQuery(GetUserPagePreferenceDocument, {
    context: {
      uri: PURCHASE_PLUS_GRAPHQL_API,
    },
    variables: {
      userId: userId || Number(user?.id),
      key,
    },
    skip: skip || !!cachedPreference,
    fetchPolicy: 'cache-first',
  })

  const pagePreference = cachedPreference || data?.user?.pagePreference

  return {
    pagePreference,
    loading: !cachedPreference && loading,
    error,
  }
}
